import React, { useState, useEffect } from "react";
import { Alert } from "react-bootstrap";
import { PagoService } from "../../../application/services/PagoService";
import DashboardLayout from "../../layouts/DashboardLayout";
import PagoSearch from "./components/PagoSearch";
import PagoList from "./components/PagoList";
import PagoConfirmModal from "./components/PagoConfirmModal";
import PagoPrintModal from "./components/PagoPrintModal";
import PagoSuccessModal from "./components/PagoSuccessModal";
import { Plus, RefreshCw } from "lucide-react";
import Button from "../../components/ui/Button";

const PagosPage = () => {
  // Estado Principal
  const [pagos, setPagos] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [showPendingOnly, setShowPendingOnly] = useState(true);
  const [totalPagos, setTotalPagos] = useState(0);

  // Selección y Modales
  const [selectedRows, setSelectedRows] = useState({});
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [showPrintModal, setShowPrintModal] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [successData, setSuccessData] = useState({ count: 0, total: 0 });

  const [selectedPago, setSelectedPago] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState("");

  const totalPagarSeleccionado = Object.values(selectedRows)
    .reduce(
      (total, p) => total + parseFloat(p.valores?.total || p.total_pagar || 0),
      0,
    )
    .toFixed(2);

  const countSeleccionados = Object.keys(selectedRows).length;

  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(100);

  // Cargar pendientes al inicio
  useEffect(() => {
    loadPendientesGlobales(true, 1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadPendientesGlobales = async (filterPending = true, page = currentPage, search = searchTerm) => {
    setLoading(true);
    setError("");
    try {
      const filters = { limit: itemsPerPage, page };
      if (filterPending) {
        filters.estado = "PENDIENTE";
      }
      if (search && search.trim() !== "") {
        filters.search = search.trim();
      }

      const data = await PagoService.getHistorialPagos(filters);
      setPagos(data.items || data || []);
      setTotalPagos(data.total || (Array.isArray(data) ? data.length : 0));
      setCurrentPage(data.page || page);
      setSelectedRows({});
      setShowPendingOnly(filterPending);
    } catch (err) {
      console.error(err);
      setPagos([]);
      setTotalPagos(0);
    } finally {
      setLoading(false);
    }
  };

  const handlePageChange = (newPage) => {
    loadPendientesGlobales(showPendingOnly, newPage);
  };

  const handleToggleFilter = () => {
    const newFilterState = !showPendingOnly;
    loadPendientesGlobales(newFilterState, 1);
  };

  const handleSearch = React.useCallback(
    async (term) => {
      const query = term !== undefined && term !== null ? term : searchTerm;
      setSearchTerm(query);

      if (!query || query.trim() === "") {
        loadPendientesGlobales(showPendingOnly, 1, "");
        return;
      }

      setLoading(true);
      setError("");
      setSelectedRows({});

      try {
        const resultados = await PagoService.consultarDeuda(query.trim());
        if (resultados && resultados.length > 0) {
          const pendientes = resultados.filter(
            (p) => p.estado !== "PAGADO" && p.estado_pago !== "Pagado",
          );
          if (showPendingOnly && pendientes.length > 0) {
            setPagos(pendientes);
            setTotalPagos(pendientes.length);
          } else {
            setPagos(resultados);
            setTotalPagos(resultados.length);
          }
        } else {
          const data = await PagoService.getHistorialPagos({
            limit: itemsPerPage,
            page: 1,
            search: query.trim(),
            estado: showPendingOnly ? "PENDIENTE" : undefined,
          });
          setPagos(data.items || data || []);
          setTotalPagos(data.total || (Array.isArray(data) ? data.length : 0));
        }
      } catch (err) {
        console.warn("Fallback a búsqueda general por:", err);
        try {
          const data = await PagoService.getHistorialPagos({
            limit: itemsPerPage,
            page: 1,
            search: query.trim(),
            estado: showPendingOnly ? "PENDIENTE" : undefined,
          });
          setPagos(data.items || data || []);
          setTotalPagos(data.total || (Array.isArray(data) ? data.length : 0));
        } catch (innerErr) {
          console.error(innerErr);
          setPagos([]);
          setTotalPagos(0);
          setError("No se encontraron registros coincidentes.");
        }
      } finally {
        setLoading(false);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [searchTerm, showPendingOnly, itemsPerPage],
  );

  const toggleSelection = (index) => {
    const pago = pagos[index];
    if (pago.estado === "PAGADO" || pago.estado_pago === "Pagado") return;

    setSelectedRows((prev) => {
      const newState = { ...prev };
      const isCurrentlySelected = Boolean(newState[index]);

      // Si es un consumo de agua, aplicar regla de selección secuencial FIFO en UI
      if (pago.tipo === "CONSUMO" && pago.id_medidor) {
        const medidorConsumos = pagos
          .map((p, idx) => ({ ...p, originalIdx: idx }))
          .filter(
            (p) =>
              p.tipo === "CONSUMO" &&
              p.id_medidor === pago.id_medidor &&
              p.estado !== "PAGADO" &&
              p.estado_pago !== "Pagado",
          )
          .sort((a, b) => {
            const dateA = new Date(a.fecha_emision || a.fecha_lectura || 0).getTime();
            const dateB = new Date(b.fecha_emision || b.fecha_lectura || 0).getTime();
            return dateA - dateB; // Más antiguo a más reciente
          });

        const targetPos = medidorConsumos.findIndex((p) => p.originalIdx === index);

        if (!isCurrentlySelected) {
          // Seleccionar este consumo y todos los anteriores (más antiguos) del medidor
          for (let i = 0; i <= targetPos; i++) {
            const item = medidorConsumos[i];
            newState[item.originalIdx] = pagos[item.originalIdx];
          }
        } else {
          // Deseleccionar este consumo y todos los posteriores (más nuevos) del medidor
          for (let i = targetPos; i < medidorConsumos.length; i++) {
            const item = medidorConsumos[i];
            delete newState[item.originalIdx];
          }
        }
        return newState;
      }

      // Para deudas extras u otros tipos
      if (isCurrentlySelected) {
        delete newState[index];
      } else {
        newState[index] = pago;
      }
      return newState;
    });
  };

  const handleConfirmPago = async (updatedRowsFromModal) => {
    setIsProcessing(true);
    try {
      const rows = updatedRowsFromModal || Object.values(selectedRows);

      // 1. Ordenar filas cronológicamente (más antiguas primero) para garantizar secuencia FIFO
      const sortedRows = [...rows].sort((a, b) => {
        const dateA = new Date(a.fecha_emision || a.fecha_lectura || 0).getTime();
        const dateB = new Date(b.fecha_emision || b.fecha_lectura || 0).getTime();
        return dateA - dateB;
      });

      // 2. Procesar pagos de manera secuencial para evitar condiciones de carrera en el servidor
      const resultados = [];
      for (const item of sortedRows) {
        if (item.tipo === "CONSUMO") {
          const res = await PagoService.registrarPago({
            id_medidor: item.medidor?.id_medidor || item.id_medidor,
            id_consumo: item.id_consumo,
            monto_pago: item.monto_pago,
            deudasExtrasSeleccionadas: [], // El consumo solo paga agua pura
            numero_comprobante: "",
            fecha_pago: new Date().toISOString(),
          });
          resultados.push(res);
        } else if (item.tipo === "EXTRA" && parseFloat(item.monto_pago || 0) > 0) {
          const res = await PagoService.registrarPago({
            id_medidor: item.medidor?.id_medidor || item.id_medidor,
            id_deuda_detalle: item.id_deuda_detalle,
            monto_pago: item.monto_pago,
            numero_comprobante: "",
            fecha_pago: new Date().toISOString(),
          });
          resultados.push(res);
        }
      }

      // Correlacionar resultados exitosos con sus filas originales
      const pagosCompletos = resultados
        .map((res, i) => {
          if (!res) return null;
          const originalRow = sortedRows[i];

          // Asegurar que el objeto valores refleje la realidad del pago realizado
          const mora = parseFloat(res.mora || 0);
          const reconexion = parseFloat(res.reconexion || 0);
          const costos = res.costos || {};
          const subtotalCalculado = parseFloat(res.subtotal || 0);
          const deudaExtraPagada = parseFloat(
            res.deuda_extra || res.deudaExtra || 0,
          );
          const totalFinal = parseFloat(
            res.consumo_total || res.consumoTotal || 0,
          );
          const detExtraRaw = res.det_extra_raw;

          return {
            ...originalRow,
            ...res,
            valores: {
              mora: mora,
              reconexion: reconexion,
              valorExceso: parseFloat(costos.valorExceso || 0),
              subtotal: subtotalCalculado, // Consumo base
              deuda_extra: deudaExtraPagada, // Deuda pagada (IMPORTANTE: Mostrar lo que se pagó)
              total: totalFinal, // Suma total
            },
            lista_deudas_extras: detExtraRaw
              ? [
                  {
                    titulo: detExtraRaw,
                    valor_pendiente: 0, // Al estar pagado, el pendiente de ESTE registro es 0
                    valor_pagado: deudaExtraPagada,
                  },
                ]
              : [],
            monto_pagado_item: originalRow.monto_pago,
          };
        })
        .filter((p) => p !== null);

      const totalPagado = rows
        .reduce((acc, p) => acc + parseFloat(p.monto_pago || 0), 0)
        .toFixed(2);

      // Si todos los pagos son para el mismo cliente, crear un "Recibo Maestro"
      const firstPago = pagosCompletos[0];
      const allSameClient = pagosCompletos.every(
        (p) => p.id_cliente === firstPago.id_cliente,
      );

      let recibosFinales = [];
      if (allSameClient && pagosCompletos.length > 1) {
        // Crear recibo consolidado
        recibosFinales = [
          {
            ...firstPago,
            is_consolidated: true,
            items_pagados: pagosCompletos.map((p) => ({
              tipo: p.tipo,
              descripcion:
                p.tipo === "CONSUMO"
                  ? `Consumo ${p.mes ? p.mes.toUpperCase() : "AGUA"}`
                  : (p.descripcion?.startsWith("Abono") ? p.descripcion : `Abono: ${p.descripcion || "Deuda Extra"}`),
              monto: p.monto_pagado_item,
              id: p.id_consumo || p.id_deuda_detalle,
            })),
            total_pagar: totalPagado,
            numero_comprobante: pagosCompletos
              .map((p) => p.numeroComprobante || "---")
              .join(" / "),
          },
        ];
      } else {
        recibosFinales = pagosCompletos;
      }

      setSuccessData({
        count: resultados.length,
        total: totalPagado,
        pagos: recibosFinales,
      });

      setShowSuccessModal(true);
      setShowConfirmModal(false);
      setSelectedRows({});

      if (searchTerm) {
        handleSearch(null);
      } else {
        loadPendientesGlobales(showPendingOnly);
      }
    } catch (err) {
      console.error(err);
      alert("Error al procesar pagos: " + err.toString());
    } finally {
      setIsProcessing(false);
    }
  };

  const handlePrintClick = (pago) => {
    setSelectedPago(pago);
    setShowPrintModal(true);
  };

  const handleSuccessModalClose = () => {
    setShowSuccessModal(false);
  };

  const handleSuccessPrint = () => {
    if (successData.pagos && successData.pagos.length > 0) {
      const pagoImprimir = successData.pagos[0];
      setSelectedPago(pagoImprimir);
      setShowSuccessModal(false);
      setTimeout(() => setShowPrintModal(true), 300);
    } else {
      setShowSuccessModal(false);
    }
  };

  const handlePrintAction = () => {
    const printWindow = window.open("", "", "width=800,height=600");
    const content = document.getElementById("comprobante-content").innerHTML;

    printWindow.document.write(`
            <html>
                <head>
                    <title>Comprobante</title>
                    <style>
                        body { 
                            font-family: monospace; 
                            padding: 0; 
                            margin: 0 auto; 
                            width: 280px; 
                            font-size: 14px; 
                            line-height: 1.4; 
                            font-weight: bold;
                            color: #000;
                        }
                        h3, h5 { margin: 5px 0; text-align: center; font-size: 16px; text-transform: uppercase; }
                        .info-row { display: flex; justify-content: space-between; margin: 4px 0; }
                        .line { border-bottom: 3px solid #000; margin: 10px 0; }
                        .text-center { text-align: center; margin-bottom: 10px; }
                        .barcode { text-align: center; margin-top: 10px; }
                        img { 
                            max-height: 50px; 
                            width: auto; 
                            display: block; 
                            margin: 5px auto; 
                            filter: grayscale(100%) contrast(200%);
                            -webkit-filter: grayscale(100%) contrast(200%);
                        }
                        img.sello { max-height: 25px; margin-top: 5px; }
                    </style>
                </head>
                <body>
                    ${content}
                    <script>window.print(); window.close();</script>
                </body>
            </html>
        `);
    printWindow.document.close();
    setShowPrintModal(false);
  };

  const renderEstado = (estado) => {
    const statusUpper = (estado || "PENDIENTE").toUpperCase();
    let colorClass = "bg-gray-100 text-gray-800";

    if (statusUpper === "PAGADO") colorClass = "bg-green-100 text-green-800";
    else if (statusUpper === "PENDIENTE")
      colorClass = "bg-amber-100 text-amber-800";
    else if (statusUpper === "MORA") colorClass = "bg-red-100 text-red-800";

    return (
      <span
        className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${colorClass}`}
      >
        {statusUpper}
      </span>
    );
  };

  return (
    <DashboardLayout title="Gestión de Pagos">
      <div className="space-y-6 animate-fade-in">
        {/* Top Action Bar */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 tracking-tight">
              Gestión de Pagos
            </h1>
            <p className="text-sm text-gray-500 mt-1">
              Consulta el estado de cuenta y registra recaudaciones.
            </p>
          </div>
          <div className="flex gap-2">
            <Button
              onClick={handleToggleFilter}
              className={`${showPendingOnly ? "bg-amber-600 hover:bg-amber-700" : "bg-green-600 hover:bg-green-700"} text-white border-none shadow-sm flex items-center gap-2 transition-colors duration-200`}
              title={
                showPendingOnly
                  ? "Clic para ver todos los registros"
                  : "Clic para ver solo pendientes"
              }
            >
              <RefreshCw className="w-4 h-4" />
              {showPendingOnly ? "Ver Todos" : "Consultar Pendientes"}
            </Button>
            <Button
              onClick={() => setShowConfirmModal(true)}
              disabled={countSeleccionados === 0}
              variant="primary"
              className="flex items-center gap-2 shadow-sm"
            >
              <Plus className="w-4 h-4" />
              Pagar ({countSeleccionados})
            </Button>
          </div>
        </div>

        <PagoSearch
          searchTerm={searchTerm}
          setSearchTerm={setSearchTerm}
          handleSearch={handleSearch}
          loading={loading}
          loadHistorial={() => loadPendientesGlobales(showPendingOnly)}
        />

        {error && (
          <Alert variant="danger" className="mb-4">
            {error}
          </Alert>
        )}

        <PagoList
          pagos={pagos}
          totalResults={totalPagos}
          currentPage={currentPage}
          itemsPerPage={itemsPerPage}
          onPageChange={handlePageChange}
          selectedRows={selectedRows}
          toggleSelection={toggleSelection}
          handlePrintClick={handlePrintClick}
          loading={loading}
          renderEstado={renderEstado}
        />

        <PagoConfirmModal
          show={showConfirmModal}
          onHide={() => setShowConfirmModal(false)}
          selectedRows={selectedRows}
          totalPagar={totalPagarSeleccionado}
          isProcessing={isProcessing}
          onConfirm={handleConfirmPago}
        />

        <PagoSuccessModal
          isOpen={showSuccessModal}
          onClose={handleSuccessModalClose}
          onPrint={handleSuccessPrint}
          cantidadPagos={successData.count}
          totalPagado={successData.total}
        />

        <PagoPrintModal
          show={showPrintModal}
          onHide={() => setShowPrintModal(false)}
          selectedPago={selectedPago}
          handlePrintAction={handlePrintAction}
        />
      </div>
    </DashboardLayout>
  );
};

export default PagosPage;
